import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, NavigationEnd, Route, Router, RouterLink, RouterLinkActive, Routes } from '@angular/router';

import { take } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AuthService } from '../../../core/services/auth.service';
import { CurrentUserService } from '../../../core/services/current-user.service';
import { PortalThemeService } from '../../../core/services/portal-theme.service';
import { SessionService } from '../../../core/services/session.service';
import { TermsService } from '../../../core/services/terms.service';
import { ProfileService } from '../../../features/profile/services/profile.service';
import { BottomNavComponent, BottomNavTab } from '../bottom-nav/bottom-nav.component';
import { IconComponent, IconName } from '../icon/icon.component';
import { TcAcceptanceDialogComponent } from '../tc-acceptance-dialog/tc-acceptance-dialog.component';
import { ToastHostComponent } from '../toast-host/toast-host.component';

export interface NavItem {
  label: string;
  icon: IconName;
  route: string;
  badge?: string | number;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

const DESKTOP_BP = '(min-width: 1024px)';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    IconComponent,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    BottomNavComponent,
    TcAcceptanceDialogComponent,
    ToastHostComponent
  ],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'closeProfile()',
    '(document:keydown.escape)': 'closeProfile()'
  }
})
export class AppLayoutComponent implements OnInit {
  readonly sidenav = viewChild<MatSidenav>('sidenav');

  sections = input<NavSection[]>([]);
  moduleLabel = input('');
  logoutRoute = input('');

  /**
   * Primary mobile tabs — subset of `sections` surfaced at the bottom of the
   * viewport on screens below the desktop breakpoint (plan §11).
   */
  readonly bottomNavTabs: BottomNavTab[] = [
    { label: 'Home', icon: 'home', route: '/dashboard' },
    { label: 'Carts', icon: 'package', route: '/carts' },
    { label: 'Items', icon: 'box', route: '/items' },
    { label: 'Payouts', icon: 'dollar', route: '/payouts' }
  ];

  profileOpen = signal(false);

  private readonly prefetched = new Set<string>();

  closeProfile(): void {
    this.profileOpen.set(false);
  }

  /**
   * Warm the lazy chunk for a route on hover so a subsequent click navigates
   * without the chunk-load delay. Idempotent — a path is loaded at most once.
   */
  protected prefetch(path: string): void {
    if (this.prefetched.has(path)) return;
    const target = path.replace(/^\//, '');
    const route = this.findRoute(this.router.config, target);
    const load = route?.loadComponent;
    if (load) {
      this.prefetched.add(path);
      void load();
    }
  }

  private findRoute(routes: Routes, target: string): Route | undefined {
    for (const r of routes) {
      if (r.path === target) return r;
      if (r.children) {
        const found = this.findRoute(r.children, target);
        if (found) return found;
      }
    }
    return undefined;
  }

  onTcAccepted(): void {
    // Record the acceptance (mock updates locally; real POSTs to
    // ConsignorMe/Terms/Accept) and refresh the gate so the overlay closes.
    this.acceptingTc.set(true);
    this.terms
      .acceptActiveVersion()
      .pipe(
        take(1),
        finalize(() => this.acceptingTc.set(false))
      )
      .subscribe(gate => this.currentUser.setTcGate(gate));
  }

  onTcCancelled(): void {
    // A consignor who won't accept the updated terms can't use the shell —
    // sign them out rather than trap them behind the blocking overlay.
    this.signOut();
  }

  protected readonly currentUser = inject(CurrentUserService);
  private readonly terms = inject(TermsService);
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly profileService = inject(ProfileService);
  private readonly bpObserver = inject(BreakpointObserver);
  private readonly document = inject(DOCUMENT);
  activeCrumb = signal('');
  isDesktop = signal(false);
  protected readonly checkingTcGate = signal(false);
  protected readonly acceptingTc = signal(false);

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly portalTheme = inject(PortalThemeService);

  protected profileSubtitle = computed(() => {
    return this.currentUser.email();
  });

  ngOnInit() {
    this.activeCrumb.set(this.getRouteTitle());
    this.loadConsignorName();
    this.loadTcGate();

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.activeCrumb.set(this.getRouteTitle());
        this.profileOpen.set(false);
        if (!this.isDesktop() && this.sidenav()) {
          this.sidenav()!.close();
        }
        const main = this.document.getElementById('main-content');
        if (main) {
          main.focus();
        }
      });

    this.bpObserver
      .observe(DESKTOP_BP)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.isDesktop.set(result.matches);
      });
  }

  /**
   * In real-API mode, fetch the consignor's T&C gate after login so the
   * re-acceptance overlay can block the shell when a material change is pending.
   * Mock mode populates the gate at login via DevAuthService, so this is skipped.
   */
  private loadTcGate(): void {
    if (environment.useMockData || !this.authService.isAuthenticated()) return;
    this.checkingTcGate.set(true);
    this.terms
      .getGate()
      .pipe(
        take(1),
        finalize(() => this.checkingTcGate.set(false))
      )
      .subscribe({
        next: gate => this.currentUser.setTcGate(gate),
        error: () => {
          /* non-fatal — the shell still renders without the gate */
        }
      });
  }

  private loadConsignorName(): void {
    if (!this.authService.isAuthenticated()) return;
    this.profileService
      .getProfile()
      .pipe(take(1))
      .subscribe({
        next: p => {
          if (p.fullName) this.currentUser.setApiDisplayName(p.fullName);
          // Authoritative suspension source — unlike the T&C gate, this doesn't
          // 404 (and get treated as unrestricted) when no active Terms version
          // is configured for the company.
          this.currentUser.setConsignorStatus(p.consignorStatus);
        }
      });
  }

  signOut(): void {
    this.sessionService.signOut(this.logoutRoute() || '/login');
  }

  private getRouteTitle(): string {
    let r = this.route.root;
    let label = '';
    while (r.firstChild) {
      r = r.firstChild;
      if (r.snapshot.data['breadcrumb']) label = r.snapshot.data['breadcrumb'];
    }
    return label || this.moduleLabel();
  }
}
